import axios from 'axios'
import type {
    AxiosError,
    AxiosRequestConfig,
    AxiosResponse,
    InternalAxiosRequestConfig,
} from 'axios'
import { useAuthStore } from '@/stores/auth'

const rawBaseUrl = import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000/api/'
export const baseURL = rawBaseUrl.endsWith('/') ? rawBaseUrl : `${rawBaseUrl}/`

export const api = axios.create({
    baseURL,
    headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache',
    },
    withCredentials: true,
})

// Request Interceptor: Attach bearer token & cache-busting headers
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('accessToken')
    if (token && config.headers && !config.url?.includes('token/refresh')) {
        config.headers.Authorization = `Bearer ${token}`
    }
    // Prevent browser caching on dynamic API queries
    if (config.headers) {
        config.headers['Cache-Control'] = 'no-cache'
        config.headers['Pragma'] = 'no-cache'
    }
    return config
})

// Response Interceptor & Unified Token Refresh Logic
let isRefreshing = false

type FailedRequest = {
    resolve: (token: string) => void
    reject: (err: AxiosError) => void
}

let failedQueue: FailedRequest[] = []

const processQueue = (error: AxiosError | null, token: string | null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error)
        } else {
            prom.resolve(token!)
        }
    })
    failedQueue = []
}

api.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
        const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean }

        // Do not attempt refresh on auth endpoints (login, refresh)
        const isAuthEndpoint = originalRequest?.url?.includes('token/refresh') || originalRequest?.url?.includes('auth/login')

        if (error.response?.status === 401 && !originalRequest?._retry && !isAuthEndpoint) {
            originalRequest._retry = true
            const refreshToken = localStorage.getItem('refreshToken')

            if (!refreshToken) {
                const authStore = useAuthStore()
                authStore.clearAuth()
                return Promise.reject(error)
            }

            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({
                        resolve: (token: string) => {
                            if (originalRequest.headers) {
                                originalRequest.headers.Authorization = `Bearer ${token}`
                            }
                            resolve(api(originalRequest))
                        },
                        reject: (err: AxiosError) => reject(err),
                    })
                })
            }

            isRefreshing = true

            try {
                // Use a clean axios instance for refresh to avoid interceptor loops
                const refreshUrl = `${baseURL}token/refresh/`
                const response = await axios.post(refreshUrl, {
                    refresh: refreshToken,
                })

                const newAccessToken = response.data.access
                const newRefreshToken = response.data.refresh

                localStorage.setItem('accessToken', newAccessToken)
                if (newRefreshToken) {
                    localStorage.setItem('refreshToken', newRefreshToken)
                }

                api.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`
                if (originalRequest.headers) {
                    originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
                }

                processQueue(null, newAccessToken)
                return api(originalRequest)
            } catch (refreshError) {
                processQueue(refreshError as AxiosError, null)
                const authStore = useAuthStore()
                authStore.clearAuth()
                return Promise.reject(refreshError)
            } finally {
                isRefreshing = false
            }
        }

        return Promise.reject(error)
    },
)

export default api
